import { ApiMenuData, CreateCartRequestBody, CreateCartResponse, AddCartPaymentRequestBody, AddCartPaymentResponse, PaymentStatusResponse, OnboardingScreensResponse, LoyaltyDiscoveryRequestBody, LoyaltyDiscoveryResponse, SubmitCartResponse } from '../types';
import { getMenuEndpoint, getApiHeaders, getCreateCartEndpoint, getAddCartPaymentEndpoint, getSubmitCartEndpoint, getPaymentStatusEndpoint, getLoyaltyDiscoveryEndpoint, CMS_ONBOARDING_SCREENS_URL, CMS_SETTINGS_BY_KEY_BASE_URL } from '../config/api';
import testData from '../data/test.json';

export class ApiService {
  private static instance: ApiService;

  private constructor() {}

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /**
   * Fetch menu data from the API
   */
  async fetchMenuData(): Promise<ApiMenuData> {
    try {
      const endpoint = getMenuEndpoint();
      const headers = getApiHeaders();

      console.log('Fetching menu data from:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const data: ApiMenuData = await response.json();
      
      console.log('Menu data fetched successfully:', {
        totalRecords: data.totalRecords,
        numberOfRecords: data.numberOfRecords
      });

      return data;
    } catch (error) {
      console.error('Error fetching menu data from API:', error);
      
      // Fallback to test data if API fails
      console.log('Falling back to test data...');
      return testData as ApiMenuData;
    }
  }

  /**
   * Fetch kiosk onboarding screens from CMS (welcome/loyalty images).
   */
  async fetchOnboardingScreens(): Promise<OnboardingScreensResponse> {
    const response = await fetch(CMS_ONBOARDING_SCREENS_URL, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Onboarding screens API error: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Fetch a single kiosk setting from CMS by key.
   */
  async fetchCmsSettingByKey(key: string): Promise<string> {
    const response = await fetch(`${CMS_SETTINGS_BY_KEY_BASE_URL}/${key}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`CMS setting fetch failed for ${key}: ${response.status}`);
    }

    const data: { value?: string } = await response.json();
    const rawValue = data.value ?? '';
    return this.parseCmsSettingValue(rawValue);
  }

  /**
   * CMS values can come wrapped in different HTML shapes
   * (e.g. <pre><code>...</code></pre> or <p>...</p>).
   * Normalize them into plain text.
   */
  private parseCmsSettingValue(rawValue: string): string {
    return rawValue
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Get test data (for development/testing)
   */
  getTestData(): ApiMenuData {
    return testData as ApiMenuData;
  }

  /**
   * Check if API is available
   */
  async checkApiHealth(): Promise<boolean> {
    try {
      const endpoint = getMenuEndpoint();
      const headers = getApiHeaders();

      const response = await fetch(endpoint, {
        method: 'HEAD', // Use HEAD to check without downloading full data
        headers,
      });

      return response.ok;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }

  /**
   * Create cart/order from selected items (POST /carts/orders).
   * Returns cartId for use in subsequent payment/order API calls.
   */
  async createCart(body: CreateCartRequestBody): Promise<CreateCartResponse> {
    const endpoint = getCreateCartEndpoint();
    const headers = getApiHeaders();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const data: CreateCartResponse = await response.json();

    if (!response.ok) {
      const message = data.notifications?.[0]?.text ?? data.message ?? `HTTP ${response.status}`;
      throw new Error(message);
    }

    return data;
  }

  /**
   * Add a payment to an existing cart (POST /carts/orders/:cartId/payments).
   * Returns paymentId.
   */
  async addCartPayment(cartId: string, body: AddCartPaymentRequestBody): Promise<AddCartPaymentResponse> {
    const endpoint = getAddCartPaymentEndpoint(cartId);
    const headers = getApiHeaders();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const data: AddCartPaymentResponse = await response.json();

    if (!response.ok) {
      const message = data.notifications?.[0]?.text ?? data.message ?? `HTTP ${response.status}`;
      throw new Error(message);
    }

    return data;
  }

  /**
   * Submit an existing cart (POST /carts/orders/:cartId/submit).
   * Returns orderId.
   */
  async submitCart(cartId: string): Promise<SubmitCartResponse> {
    const endpoint = getSubmitCartEndpoint(cartId);
    const headers = getApiHeaders();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
    });

    const data: SubmitCartResponse = await response.json();

    if (!response.ok) {
      const message = data.notifications?.[0]?.text ?? data.message ?? `HTTP ${response.status}`;
      throw new Error(message);
    }

    return data;
  }

  /**
   * Loyalty discovery (POST /Customers/Loyalty/Discovery).
   * Validates OTP and returns profile key, brand group key, and first name.
   */
  async loyaltyDiscovery(body: LoyaltyDiscoveryRequestBody): Promise<LoyaltyDiscoveryResponse> {
    const endpoint = getLoyaltyDiscoveryEndpoint();
    const headers = getApiHeaders();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const data: LoyaltyDiscoveryResponse = await response.json();

    if (!response.ok) {
      const message = data.notifications?.[0]?.text ?? data.message ?? `HTTP ${response.status}`;
      throw new Error(message);
    }

    return data;
  }

  /**
   * Get payment status (GET /payments/:paymentId/status).
   */
  async getPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    const endpoint = getPaymentStatusEndpoint(paymentId);
    const headers = getApiHeaders();

    const response = await fetch(endpoint, {
      method: 'GET',
      headers,
    });

    const data: PaymentStatusResponse = await response.json();

    if (!response.ok) {
      throw new Error(data.message ?? `HTTP ${response.status}`);
    }

    return data;
  }
}

// Export singleton instance
export const apiService = ApiService.getInstance();
export default apiService; 